import { Theme } from '@emotion/react';
import useStylesheets from '@vromo/hooks/useStylesheets';
import useTheme from '@vromo/hooks/useTheme';
import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import { qaLabelTrackingLink } from '../../../common-qa-labels/tracking-link';
import TrackingLinkStore from '../TrackingLinkStore';
import SpatulaSignedJobView from '../WeDispatch/SpatulaSignedJobView.css';
import '../locales/i18nTracking';
import Header from './Header';
import OrderTrackingMobile from './OrderTrackingMobile';
import Widget from './Widget';
import ETADisplay from './components/ETADisplay/ETADisplay';
import MapWrapper from './components/MapWrapper/MapWrapper';
import OrderDetails from './components/OrderDetails/OrderDetails';
import OrderProgress from './components/OrderProgress';
import OrderProgressBar from './components/OrderProgress/components/OrderProgressBar';
import styles, { cssStyles } from './orderTracking.style';
import useUIState, { UIStateInterface } from './useUIState';

smoothscroll.polyfill();

interface Props {
    host: string;
    auth: string;
}

const OrderTracking = observer<Props>(({ host, auth }) => {
    useStylesheets(SpatulaSignedJobView);
    const campaignTheme = useTheme();
    const rootStore = useStore<TrackingLinkStore>('rootStore');

    const [isMobile, setMobile] = useState(window.innerWidth <= 768);

    const job = rootStore.jobs.first()!;
    const vehicle = rootStore.vehicles.first()!;
    const widgets = job?.campaign?.widgets || [];

    useEffect(() => {
        const handleWindowSizeChange = () => setMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);

    const UIState: UIStateInterface = useUIState(job);

    if (isMobile) {
        return <OrderTrackingMobile {...{ host, auth, job, UIState }} />;
    }

    return (
        <div className={cssStyles.container}>
            <Header />
            <div className={cssStyles.contentContainer}>
                <div style={styles.leftContent}>
                    <ETADisplay />
                    <div style={styles.barContainer}>
                        {!job.cancelled && <OrderProgressBar progress={UIState.progress} />}
                    </div>
                    <div style={styles.title} data-testid={qaLabelTrackingLink.statusTitle}>
                        {UIState.statusTitle}
                    </div>
                    <div
                        style={styles.statusDescription}
                        data-testid={qaLabelTrackingLink.statusSubtitle}>
                        {UIState.statusDescription}
                    </div>
                    <div style={styles.mapContainer}>
                        <div style={styles.map}>
                            <MapWrapper />
                        </div>
                        <div style={styles.cardContent}>
                            <OrderProgress {...{ vehicle, host, auth }} stepIx={UIState.stepIx} />
                        </div>
                    </div>
                </div>
                <div style={styles.rightContent}>
                    <OrderDetails />
                    {job.campaign?.promotion?.enabled && (
                        <div className={cssStyles.widgetContainer}>
                            {widgets.map((widget, i) => (
                                <div key={widget.id}>
                                    <Widget
                                        key={widget.id}
                                        theme={campaignTheme as Theme}
                                        {...{ widget, vehicle, job, host, auth }}
                                    />
                                    {i < widgets.length - 1 && <div style={styles.lineDivider} />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div style={styles.bottomPadding} />
        </div>
    );
});

export default OrderTracking;
